import React, { useEffect, useState, useRef } from "react"
import classNames from "classnames"
import { List, theme } from "antd"
import { Avatar } from "ui/Avatar"
import { MessageContent } from "./MessageContent"
import { normalizeSendDate, normalizeSendDateAndTime, handleConversationSocket } from "../utils"
import PropTypes from "prop-types"
import styles from "./Message.module.css"
import { Text } from "ui/Typography"
import { useTranslation } from "react-i18next"
import { WarningIcon } from "ui/Icon/DSIcon/WarningIcon"
import { useDispatch, useSelector } from "react-redux"
import { sendMessage } from "store/conversations"
import { serialize } from "object-to-formdata"
import { useCable } from "components/Cable"

const { useToken } = theme

export const Message = ({ message, isOwnMessage, isMobile, user, conversation, subscription, setSubscription, onEdit }) => {
  const cable = useCable()
  const chanel = useRef(null)
  const { token } = useToken()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [nestedMessages, setNestedMessages] = useState(message.nestedMessages || [])
  const [sendingStatus, setSendingStatus] = useState({})
  const prevNestedMessagesRef = useRef([])
  const role = useSelector((state) => state.user.role)

  const retrySendMessage = (message) => {
    setSendingStatus((prev) => ({ ...prev, [message.id]: true }))

    const retry = true

    const resetSendingStatus = () => {
      setSendingStatus((prev) => ({ ...prev, [message.id]: false }))
    }

    const handleFinally = () => {
      setTimeout(resetSendingStatus, 3000)
    }

    dispatch(
      sendMessage(message.conversation_id, serialize({ message: { body: message.body, media: message.media, uuid: message?.uuid } }), retry)
    ).finally(handleFinally)
  }

  useEffect(() => {
    const currentNestedMessages = message.nestedMessages || []
    if (JSON.stringify(prevNestedMessagesRef.current) !== JSON.stringify(currentNestedMessages)) {
      setNestedMessages(currentNestedMessages)
      prevNestedMessagesRef.current = currentNestedMessages
    }
  }, [message.nestedMessages])

  const unviewedMessages = nestedMessages.filter((msg) => !msg.viewed)

  useEffect(() => {
    if (!isOwnMessage) {
      unviewedMessages.forEach(() => {
        try {
          subscription?.conversationSubscription.send({
            event: "view_messages",
            conversation_id: message?.conversation_id,
            author_id: message?.author_id,
            current_user: user
          })
        } catch (error) {
          console.error("Error sending view_messages event:", error)
        }
      })
    }
  }, [unviewedMessages.length])

  useEffect(() => {
    handleConversationSocket({
      cable,
      conversation,
      role,
      chanel,
      setSubscription,
      dispatch
    })
  }, [])

  useEffect(() => {
    return () => {
      if (chanel.current?.conversationSubscription) {
        chanel.current.conversationSubscription.unsubscribe()
      }
      if (chanel.current?.statusSubscription) {
        chanel.current.statusSubscription.unsubscribe()
      }
    }
  }, [])

  return (
    <List.Item className={classNames(styles.messageWrapper, isMobile && styles.isMobile)} data-message-id={message?.id}>
      <List.Item.Meta
        description={
          <>
            {message.isFirstMessageOfNewDay && (
              <div className="justify-center mb-m">
                <Text variant="caption" type="secondary">
                  {normalizeSendDate(message.created_at)}
                </Text>
              </div>
            )}
            <div className={classNames("flex flex-grow-1", isOwnMessage && "justify-end", "mb-xxs")}>
              <div className={styles.avatarWrapper} />
              <div className="align-center"></div>
            </div>
            <div
              className={classNames(
                "flex flex-grow-1",
                isOwnMessage && styles.ownMessage,
                message.sameAuthorMessage && styles.sameAuthorMessage
              )}
            >
              <div className={classNames(styles.messageBody)}>
                <div className={styles.messages}>
                  {!isOwnMessage && (
                    <>
                      <div className={styles.avtarZone}>
                        <Text className={styles.timeText} variant="caption" type="secondary">
                          {message.author?.full_name}
                        </Text>
                        <Text className={styles.timeText}>{normalizeSendDateAndTime(message.created_at)}</Text>
                        {message.is_edited && <Text className={classNames(styles.timeText, styles.editedText)}>{t("global.edited")}</Text>}
                      </div>
                      <div className={styles.avtarContent}>
                        <div className={styles.avatarTime}>
                          {isOwnMessage ? null : (
                            <Avatar src={message?.author?.avatar} fullName={message.author?.full_name} size={isMobile ? 32 : 52} />
                          )}
                        </div>
                        <MessageContent message={message} isOwnMessage={isOwnMessage} />
                      </div>
                    </>
                  )}
                  {isOwnMessage && (
                    <>
                      <div className="self-end">
                        <Text className={styles.timeText}>{normalizeSendDateAndTime(message.created_at)}</Text>
                        {message?.is_edited && (
                          <Text className={classNames(styles.statusText, styles.editedText)}>{t("global.edited")}</Text>
                        )}
                      </div>
                      <MessageContent message={message} isOwnMessage={isOwnMessage} />
                    </>
                  )}
                  {nestedMessages?.length > 0 &&
                    nestedMessages.map((msg, index) => {
                      let messageStatus = null

                      if (index === nestedMessages.length - 1 && !msg.failed) {
                        messageStatus = msg.viewed ? t("global.read") : t("global.delivered")
                      }

                      return (
                        <React.Fragment key={msg.id}>
                          {!isOwnMessage && (
                            <div className={styles.avtarZone}>
                              <Text className={styles.timeText} variant="caption" type="secondary">
                                {message.author?.full_name}
                              </Text>
                              <Text className={styles.timeText}>{normalizeSendDateAndTime(msg.created_at)}</Text>
                              {msg.is_edited && (
                                <Text className={classNames(styles.statusText, styles.editedText, styles.editedClientText)}>
                                  {t("global.edited")}
                                </Text>
                              )}
                            </div>
                          )}
                          {isOwnMessage && (
                            <div className="self-end">
                              <Text className={styles.timeText}>{normalizeSendDateAndTime(msg.created_at)}</Text>
                              {msg.is_edited && (
                                <Text className={classNames(styles.statusText, styles.editedText)}>{t("global.edited")}</Text>
                              )}
                            </div>
                          )}
                          {!isOwnMessage && (
                            <div className={styles.avtarContent}>
                              <div className={styles.avatarTime}>
                                {isOwnMessage ? null : (
                                  <Avatar src={message?.author?.avatar} fullName={message?.author?.full_name} size={isMobile ? 32 : 52} />
                                )}
                              </div>
                              <MessageContent message={msg} isOwnMessage={isOwnMessage} date={normalizeSendDateAndTime(msg.created_at)} />
                            </div>
                          )}
                          {isOwnMessage && (
                            <div className={styles.warningContent}>
                              <MessageContent
                                message={msg}
                                isOwnMessage={isOwnMessage}
                                date={normalizeSendDateAndTime(msg.created_at)}
                                onEdit={onEdit}
                              />
                              <Text className={styles.statusText}>{messageStatus}</Text>
                            </div>
                          )}
                          {isOwnMessage && msg.failed && (
                            <>
                              {sendingStatus[msg.id] ? (
                                <Text className={styles.timeText}>{t("messenger.sending")}</Text>
                              ) : (
                                <div className={styles.retryContent}>
                                  {msg.failed && (
                                    <WarningIcon className={styles.errorIcon} fill={token.colorPrimary} style={styles.defaultImage} />
                                  )}
                                  <Text className={styles.failedText} type="danger">
                                    {t("messenger.errors.failedToSend")}
                                  </Text>
                                  <button className={styles.retryButton} onClick={() => retrySendMessage(msg)}>
                                    {t("messenger.errors.retry")}
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </React.Fragment>
                      )
                    })}
                </div>
              </div>
            </div>
          </>
        }
      />
    </List.Item>
  )
}
Message.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.string.isRequired,
    uuid: PropTypes.string.isRequired,
    body: PropTypes.string,
    author: PropTypes.shape({
      full_name: PropTypes.string.isRequired,
      avatar: PropTypes.string
    }).isRequired,
    created_at: PropTypes.string.isRequired,
    conversation_id: PropTypes.number.isRequired,
    media_type: PropTypes.string,
    media_url: PropTypes.string,
    media: PropTypes.any,
    is_edited: PropTypes.bool,
    isFirstMessageOfNewDay: PropTypes.bool,
    nestedMessages: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        viewed: PropTypes.bool,
        created_at: PropTypes.string.isRequired,
        failed: PropTypes.bool // Add `failed` if used in nestedMessages
      })
    ),
    sameAuthorMessage: PropTypes.bool,
    viewed: PropTypes.bool,
    author_id: PropTypes.string.isRequired
  }).isRequired,
  isOwnMessage: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  user: PropTypes.shape({
    id: PropTypes.string.isRequired
  }).isRequired,
  conversation: PropTypes.object.isRequired, // Add validation for `conversation`
  subscription: PropTypes.shape({
    conversationSubscription: PropTypes.shape({
      send: PropTypes.func.isRequired
    })
  }).isRequired,
  setSubscription: PropTypes.func.isRequired,
  onEdit: PropTypes.func
}
